import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Home, Gift, Clock, Ticket, Settings, HelpCircle, LogOut, Crown, Star, Sparkles } from "lucide-react";

const CustomerLayout = ({ children, title }) => {
  const { user, loyalty, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/rewards", icon: Gift, label: "Rewards" },
    { path: "/history", icon: Clock, label: "History" },
    { path: "/vouchers", icon: Ticket, label: "Vouchers" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  const getTierIcon = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return <Crown size={14} />;
      case 'platinum': return <Sparkles size={14} />;
      default: return <Star size={14} />;
    }
  };

  const getTierClass = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return 'tier-gold';
      case 'platinum': return 'tier-platinum';
      default: return 'tier-silver';
    }
  };

  return (
    <div className="page-container bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A962]/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
              </div>
              <div>
                <h1 className="text-lg font-display font-medium">
                  <span className="text-gradient-luxury">OCULUS</span>
                  <span className="text-[#5A5A5A] text-xs ml-2 tracking-widest font-elegant">ROYALE</span>
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <div className={`tier-badge ${getTierClass(loyalty?.tier)} text-xs py-1 px-2`}>
                  {getTierIcon(loyalty?.tier)}
                  <span>{loyalty?.tier || 'Silver'}</span>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-[#5A5A5A] hover:text-[#C9A962] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav md:hidden">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `mobile-nav-item ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Desktop Side Navigation */}
      <nav className="hidden md:flex fixed left-0 top-0 bottom-0 w-20 bg-[#0F0F0F] border-r border-[#252525] flex-col items-center py-6 z-50">
        {/* Logo */}
        <div className="mb-10">
          <div className="w-12 h-12 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
          </div>
        </div>
        
        {/* Nav Items */}
        <div className="flex-1 flex flex-col gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `p-3 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#C9A962]/10 text-[#C9A962] shadow-[0_0_20px_rgba(201,169,98,0.1)]' 
                    : 'text-[#5A5A5A] hover:text-white hover:bg-[#1A1A1A]'
                }`
              }
              title={item.label}
              data-testid={`nav-${item.label.toLowerCase()}-desktop`}
            >
              <item.icon size={22} />
            </NavLink>
          ))}
        </div>
        
        {/* Bottom Actions */}
        <div className="mt-auto flex flex-col gap-2">
          <NavLink
            to="/issues"
            className={({ isActive }) =>
              `p-3 rounded-lg transition-all duration-200 ${
                isActive 
                  ? 'bg-[#C9A962]/10 text-[#C9A962]' 
                  : 'text-[#5A5A5A] hover:text-white hover:bg-[#1A1A1A]'
              }`
            }
            title="Support"
            data-testid="nav-support-desktop"
          >
            <HelpCircle size={22} />
          </NavLink>
          <button
            onClick={handleLogout}
            className="p-3 rounded-lg text-[#5A5A5A] hover:text-[#D9534F] hover:bg-[#1A1A1A] transition-colors"
            title="Logout"
            data-testid="logout-desktop-btn"
          >
            <LogOut size={22} />
          </button>
        </div>
      </nav>

      {/* Desktop content offset */}
      <style>{`
        @media (min-width: 768px) {
          .page-container > header,
          .page-container > main {
            margin-left: 80px;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerLayout;
